// SuperAdminLogin.jsx - Updated with Forgot Password Link
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, User, Eye, EyeOff,KeyRound,Mail,ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { BASE_URL } from '../lib/api';
export default function SuperAdminForgotPassword() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);
    setError(null);

    // Basic email validation
    if (!email.trim()) {
      setError('Email address is required.');
      setLoading(false);
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    try {
      console.log('SuperAdmin forgot password request for:', email);

      // Use the forgot-password endpoint with superadmin flag
      const response = await fetch(`${BASE_URL}/authentication/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: email.trim(), 
          isAdminRequest: true // Flag to indicate this is a superadmin request
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Always display a generic success message for security reasons
        const successMessage = data.message ||
          "If a super admin account exists for this email, a password reset link has been sent. Please check your inbox (and spam folder).";
        
        setMessage(successMessage);
        
        toast({
          title: 'Password Reset Request Sent',
          description: 'Please check your email for further instructions.',
        });
        
        // Clear the email field after successful submission
        setEmail(''); 
        
      } else {
        // Handle specific error cases
        let errorMessage = data.message || "An unexpected error occurred. Please try again.";
        
        if (data.message && data.message.includes('does not exist')) {
          errorMessage = "No super admin account found with this email address.";
        }
        
        setError(errorMessage);
        
        toast({
          title: 'Password Reset Failed',
          description: errorMessage,
          variant: 'destructive',
        });
      }
      
    } catch (err) {
      console.error("Super admin forgot password error:", err);
      
      let errorMessage = "Network error. Please check your connection and try again.";
      
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        errorMessage = "Unable to connect to the server. Please try again later.";
      }
      
      setError(errorMessage);
      
      toast({
        title: 'Connection Error',
        description: errorMessage,
        variant: 'destructive',
      });
      
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit(e);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center animated-gradient-background p-4">
      <div className="w-full max-w-sm">
     {/* Super Admin Icon */}
            <div className="mb-8 flex w-full justify-center">
          <img
            src="./evolvv.png"
            alt="evolvv Logo"
            width={200}
            height={50}
            className="dark:invert"
          />
        </div>
                    <Card className="w-full shadow-xl bg-card/90 backdrop-blur-sm">
                          <CardHeader className="space-y-1 text-center">
         
            <CardTitle className="text-2xl text-card-foreground">
              Reset Admin Password
            </CardTitle>
            
             <CardDescription className="text-gray-600">  
              Enter your super admin email address and we'll send you a secure link to reset your password.
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-6">
              {/* Status Messages */}
              {message && (
                <div className="p-4 rounded-lg text-sm font-medium bg-green-50 text-green-700 border border-green-200">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <span>{message}</span>
                  </div>
                </div>
              )}
              
              {error && (
                <div className="p-4 rounded-lg text-sm font-medium bg-red-50 text-red-700 border border-red-200">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                </div>
              )}

              {/* Email Input */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">
                  Super Admin Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="superadmin@example.com"
                    required
                      className="pl-10 bg-background/80 text-foreground placeholder:text-muted-foreground/70"
               value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Security Notice */}
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex items-start gap-2">
                  <Shield className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-700">
                    <p className="font-medium mb-1">Security Notice:</p>
                    <p>Password reset links are only sent to verified super admin accounts and expire after 1 hour for security.</p>
                  </div>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-4 pt-6">
              {/* Submit Button */}
              <Button 
                type="submit" 
               className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 rounded-lg transition-all duration-300 transform hover:scale-105" 
                   disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Sending Reset Link...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Send Password Reset Link
                  </div>
                )}
              </Button>

              {/* Back to Login */}
              <Button
                type="button"
                variant="ghost"
                className="w-full text-blue-500 hover:text-blue-600  hover:bg-purple-50 font-medium"
                onClick={handleBackToLogin}
                disabled={loading}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Admin Login
              </Button>

              {/* Support Contact */}
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Need assistance?{' '}
                  <a 
                    href="mailto:support@revynox.com" 
                    className="font-semibold text-blue-500 hover:text-blue-600  hover:underline transition-colors"
                  >
                    Contact System Administrator
                  </a>
                </p>
              </div>
            </CardFooter>
          </form>
        </Card>

        {/* Bottom Security Notice */}
        <div className="mt-4 text-center">
          <p className="text-xs text-white/60">
            <Shield className="inline h-3 w-3 mr-1" />
            Secure super admin portal protected by enterprise-grade encryption
          </p>
        </div>
      </div>
    </div>
  );
}